import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal, isForwardDeal, isOptionDeal } from '@src/types/OTC/otcGuards';
import type { SecuritiesContextProps, Security } from '@talos/kyoko';
import {
  isFixedRateLoanProductTemplate,
  isFloatingRateLoanProductTemplate,
  isOTCForwardProductTemplate,
  isOTCOptionProductTemplate,
} from '@talos/kyoko';

/**
 * Returns both Symbol and DisplaySymbol from TemplateSymbol for lookup in different maps.
 * OTC maps are keyed by DisplaySymbol; securitiesBySymbol is keyed by Symbol.
 */
const getTemplateSymbolKeys = (templateSymbol: unknown): { symbol?: string; displaySymbol?: string } => {
  if (typeof templateSymbol === 'string') {
    return { symbol: templateSymbol, displaySymbol: templateSymbol };
  }
  const obj = templateSymbol as { Symbol?: string; DisplaySymbol?: string };
  return { symbol: obj?.Symbol, displaySymbol: obj?.DisplaySymbol };
};

/**
 * Gets the product template for a deal. Uses TemplateSymbol only - never deal.Symbol.
 * Product template has ProductDetails (QuoteAsset, PaymentCcy, etc.); deal has DealInfo.
 */
export function getProductTemplateForDeal(
  instrumentData: OTCDealData,
  context: Pick<
    SecuritiesContextProps,
    | 'securitiesBySymbol'
    | 'otcOptionsByDisplaySymbol'
    | 'otcForwardsByDisplaySymbol'
    | 'otcFixedRateLoansByDisplaySymbol'
    | 'otcFloatingRateLoansByDisplaySymbol'
  >
): Security | undefined {
  const templateSymbol =
    'TemplateSymbol' in instrumentData ? (instrumentData as { TemplateSymbol?: unknown }).TemplateSymbol : undefined;

  if (templateSymbol == null) {
    return undefined;
  }

  const { symbol, displaySymbol } = getTemplateSymbolKeys(templateSymbol);
  const keysToTry = [displaySymbol, symbol].filter((k): k is string => k != null && k !== '');

  if (keysToTry.length === 0) {
    return undefined;
  }

  if (isOptionDeal(instrumentData)) {
    for (const key of keysToTry) {
      const template = context.otcOptionsByDisplaySymbol.get(key);
      if (template && isOTCOptionProductTemplate(template)) {
        return template;
      }
    }
    for (const key of keysToTry) {
      const security = context.securitiesBySymbol.get(key);
      if (security && isOTCOptionProductTemplate(security)) {
        return security;
      }
    }
  }

  if (isForwardDeal(instrumentData)) {
    for (const key of keysToTry) {
      const template = context.otcForwardsByDisplaySymbol.get(key);
      if (template && isOTCForwardProductTemplate(template)) {
        return template;
      }
    }
    for (const key of keysToTry) {
      const security = context.securitiesBySymbol.get(key);
      if (security && isOTCForwardProductTemplate(security)) {
        return security;
      }
    }
  }

  if (isFixedRateLoanDeal(instrumentData)) {
    for (const key of keysToTry) {
      const template = context.otcFixedRateLoansByDisplaySymbol.get(key);
      if (template && isFixedRateLoanProductTemplate(template)) {
        return template;
      }
    }
    for (const key of keysToTry) {
      const security = context.securitiesBySymbol.get(key);
      if (security && isFixedRateLoanProductTemplate(security)) {
        return security;
      }
    }
  }

  if (isFloatingRateLoanDeal(instrumentData)) {
    for (const key of keysToTry) {
      const template = context.otcFloatingRateLoansByDisplaySymbol.get(key);
      if (template && isFloatingRateLoanProductTemplate(template)) {
        return template;
      }
    }
    for (const key of keysToTry) {
      const security = context.securitiesBySymbol.get(key);
      if (security && isFloatingRateLoanProductTemplate(security)) {
        return security;
      }
    }
  }

  return undefined;
}
